// features/products/pages/ProductBatchesPage.tsx
/**
 * Product Batches Page - Standalone Batch/Lot Management
 * 
 * Features:
 * - Full batch table with expiry tracking
 * - Expiry warnings: Red (expired), Amber (within 30 days), Green (good)
 * - Add batch with modal form
 * - Edit batch inline
 * - Delete with confirmation
 * - Link to supplier
 */

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import apiClient from '@/lib/api-client';
import { getProduct, ProductData, ProductBatchData } from '../services/productsApi';
import {
    ArrowLeft, Plus, Trash2, Pencil, Save, X,
    Package, Calendar, AlertTriangle, CheckCircle,
    Clock, Truck, DollarSign,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { extractApiError } from '@/lib/api-error';

interface BatchFormData {
    batchNumber: string;
    manufacturingDate: string;
    expiryDate: string;
    quantity: string;
    costPrice: string;
    supplierId: string;
    notes: string;
}

export function ProductBatchesPage() {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showAddModal, setShowAddModal] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<ProductBatchData>>({});
    const [newForm, setNewForm] = useState<BatchFormData>({
        batchNumber: '', manufacturingDate: '', expiryDate: '',
        quantity: '0', costPrice: '', supplierId: '', notes: '',
    });
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    const { data: product, isLoading } = useQuery({
        queryKey: ['product', productId],
        queryFn: () => getProduct(user!.tenantId, productId!),
        enabled: !!user?.tenantId && !!productId,
    });

    const { data: batches, refetch: refetchBatches } = useQuery<ProductBatchData[]>({
        queryKey: ['product-batches', productId],
        queryFn: async () => {
            const { data } = await apiClient.get(`/tenants/${user!.tenantId}/products/${productId}/batches`);
            return data.data || [];
        },
        enabled: !!user?.tenantId && !!productId,
    });

    const addMutation = useMutation({
        mutationFn: () => apiClient.post(`/tenants/${user!.tenantId}/products/${productId}/batches`, newForm),
        onSuccess: () => {
            refetchBatches();
            setShowAddModal(false);
            setNewForm({ batchNumber: '', manufacturingDate: '', expiryDate: '', quantity: '0', costPrice: '', supplierId: '', notes: '' });
            addToast({ type: 'success', title: 'Batch added' });
        },
        onError: (err: any) => {
            const { title, message } = extractApiError(err, 'Failed');
            addToast({ type: 'error', title, message, duration: 10000 });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ batchId, data }: { batchId: string; data: Record<string, unknown> }) =>
            apiClient.patch(`/tenants/${user!.tenantId}/products/batches/${batchId}`, data),
        onSuccess: () => { refetchBatches(); setEditingId(null); addToast({ type: 'success', title: 'Batch updated' }); },
    });

    const deleteMutation = useMutation({
        mutationFn: (batchId: string) => apiClient.delete(`/tenants/${user!.tenantId}/products/batches/${batchId}`),
        onSuccess: () => { refetchBatches(); setDeleteConfirm(null); addToast({ type: 'success', title: 'Batch deleted' }); },
    });

    const batchList = batches || [];

    const getExpiryStatus = (expiryDate: string | null): 'expired' | 'warning' | 'good' => {
        if (!expiryDate) return 'good';
        const expiry = new Date(expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry < 0) return 'expired';
        if (daysUntilExpiry <= 30) return 'warning';
        return 'good';
    };

    const getExpiryBadge = (status: 'expired' | 'warning' | 'good') => {
        switch (status) {
            case 'expired': return <Badge variant="danger" size="sm"><AlertTriangle className="w-3 h-3 mr-1" />Expired</Badge>;
            case 'warning': return <Badge variant="warning" size="sm"><Clock className="w-3 h-3 mr-1" />Expiring Soon</Badge>;
            case 'good': return <Badge variant="success" size="sm"><CheckCircle className="w-3 h-3 mr-1" />Good</Badge>;
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const startEdit = (batch: ProductBatchData) => {
        setEditingId(batch.id);
        setEditForm({
            batchNumber: batch.batchNumber,
            manufacturingDate: batch.manufacturingDate || '',
            expiryDate: batch.expiryDate || '',
            quantity: batch.quantity,
            costPrice: batch.costPrice || '',
            supplierId: batch.supplierId || '',
            notes: batch.notes || '',
        });
    };

    if (isLoading) return <div className="flex justify-center py-20"><Spinner size="xl" /></div>;
    if (!product) return <ErrorState title="Product not found" />;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => navigate(`/products/${productId}`)} className="p-2 rounded-xl hover:bg-slate-100" type="button"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
                            <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-lg"><Package className="w-5 h-5 text-white" /></div>
                            <div>
                                <h1 className="text-xl font-bold text-slate-900">{product.name}</h1>
                                <p className="text-sm text-slate-500">
                                    {batchList.length} batch{batchList.length !== 1 ? 'es' : ''}
                                    {batchList.filter(b => getExpiryStatus(b.expiryDate) === 'expired').length > 0 && (
                                        <span className="ml-2 text-rose-500 font-medium">
                                            · {batchList.filter(b => getExpiryStatus(b.expiryDate) === 'expired').length} expired
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold shadow-lg text-sm" type="button">
                            <Plus className="w-4 h-4" /> Add Batch
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto p-6 lg:p-8">
                {batchList.length === 0 ? (
                    <EmptyState icon={<Package className="w-16 h-16" />} title="No Batches"
                        description={product.hasExpiry ? "Add batch numbers to track expiry dates." : "Batches help track manufacturing lots and expiry dates."}
                        action={{ label: 'Add First Batch', onClick: () => setShowAddModal(true) }} />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead><tr className="bg-slate-50/50">
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Batch #</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Mfg Date</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Expiry</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Quantity</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">Cost</th>
                                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase">Status</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase w-32">Actions</th>
                                </tr></thead>
                                <tbody className="divide-y divide-slate-50">
                                    {batchList.map((batch) => {
                                        const expiryStatus = getExpiryStatus(batch.expiryDate);
                                        const isEditing = editingId === batch.id;
                                        const rowBg = expiryStatus === 'expired' ? 'bg-rose-50/30' : expiryStatus === 'warning' ? 'bg-amber-50/20' : '';

                                        return (
                                            <tr key={batch.id} className={clsx('group', rowBg, isEditing && 'bg-blue-50/30')}>
                                                {isEditing ? (
                                                    <>
                                                        <td className="px-4 py-2"><input type="text" value={editForm.batchNumber || ''} onChange={e => setEditForm({ ...editForm, batchNumber: e.target.value })} className="w-full px-2 py-1.5 border rounded-lg text-sm font-mono" /></td>
                                                        <td className="px-4 py-2"><input type="date" value={editForm.manufacturingDate || ''} onChange={e => setEditForm({ ...editForm, manufacturingDate: e.target.value })} className="px-2 py-1.5 border rounded-lg text-xs" /></td>
                                                        <td className="px-4 py-2"><input type="date" value={editForm.expiryDate || ''} onChange={e => setEditForm({ ...editForm, expiryDate: e.target.value })} className="px-2 py-1.5 border rounded-lg text-xs" /></td>
                                                        <td className="px-4 py-2"><input type="number" step="0.001" value={editForm.quantity || ''} onChange={e => setEditForm({ ...editForm, quantity: e.target.value })} className="w-24 px-2 py-1.5 border rounded-lg text-sm text-right" /></td>
                                                        <td className="px-4 py-2"><input type="number" step="0.01" value={editForm.costPrice || ''} onChange={e => setEditForm({ ...editForm, costPrice: e.target.value })} className="w-24 px-2 py-1.5 border rounded-lg text-sm text-right" /></td>
                                                        <td className="px-4 py-2 text-center">{getExpiryBadge(expiryStatus)}</td>
                                                        <td className="px-4 py-2 text-right">
                                                            <button onClick={() => updateMutation.mutate({ batchId: batch.id, data: editForm as Record<string, unknown> })} className="p-1.5 bg-emerald-500 text-white rounded-lg" type="button"><Save className="w-3.5 h-3.5" /></button>
                                                            <button onClick={() => setEditingId(null)} className="p-1.5 bg-slate-200 rounded-lg ml-1" type="button"><X className="w-3.5 h-3.5" /></button>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-4 py-3 text-sm font-mono font-semibold text-slate-800">{batch.batchNumber}</td>
                                                        <td className="px-4 py-3 text-sm text-slate-500">{formatDate(batch.manufacturingDate)}</td>
                                                        <td className="px-4 py-3 text-sm text-slate-500">{formatDate(batch.expiryDate)}</td>
                                                        <td className="px-4 py-3 text-sm font-semibold text-right">{parseFloat(batch.quantity || '0').toLocaleString()}</td>
                                                        <td className="px-4 py-3 text-sm text-right text-slate-600">{batch.costPrice ? `₦${parseFloat(batch.costPrice).toLocaleString()}` : '—'}</td>
                                                        <td className="px-4 py-3 text-center">{getExpiryBadge(expiryStatus)}</td>
                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button onClick={() => startEdit(batch)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600" type="button"><Pencil className="w-3.5 h-3.5" /></button>
                                                                <button onClick={() => setDeleteConfirm(batch.id)} className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-600" type="button"><Trash2 className="w-3.5 h-3.5" /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>

            {/* Add Batch Modal */}
            <AnimatePresence>
                {showAddModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 text-white flex items-center justify-between sticky top-0 rounded-t-3xl">
                                <div className="flex items-center gap-3"><Package className="w-5 h-5" /><h2 className="text-lg font-bold">Add Batch</h2></div>
                                <button onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg bg-white/20"><X className="w-4 h-4" /></button>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Batch Number *</label><input type="text" value={newForm.batchNumber} onChange={e => setNewForm({ ...newForm, batchNumber: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm font-mono" placeholder="BATCH-001" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Quantity *</label><input type="number" step="0.001" value={newForm.quantity} onChange={e => setNewForm({ ...newForm, quantity: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Mfg Date</label><input type="date" value={newForm.manufacturingDate} onChange={e => setNewForm({ ...newForm, manufacturingDate: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Expiry Date</label><input type="date" value={newForm.expiryDate} onChange={e => setNewForm({ ...newForm, expiryDate: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Cost Price</label><input type="number" step="0.01" value={newForm.costPrice} onChange={e => setNewForm({ ...newForm, costPrice: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" /></div>
                                    <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Supplier</label><input type="text" value={newForm.supplierId} onChange={e => setNewForm({ ...newForm, supplierId: e.target.value })} className="w-full px-3 py-2.5 border rounded-xl text-sm" placeholder="Supplier ID" /></div>
                                </div>
                                <div><label className="text-xs font-semibold text-slate-600 mb-1 block">Notes</label><textarea value={newForm.notes} onChange={e => setNewForm({ ...newForm, notes: e.target.value })} rows={2} className="w-full px-3 py-2.5 border rounded-xl text-sm resize-none" /></div>
                                <div className="flex justify-end gap-3 pt-4 border-t">
                                    <button onClick={() => setShowAddModal(false)} className="px-5 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                    <button onClick={() => addMutation.mutate()} disabled={!newForm.batchNumber || !newForm.quantity}
                                        className="px-6 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-semibold text-sm shadow-lg disabled:opacity-50" type="button">Add Batch</button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Delete Confirmation */}
            <AnimatePresence>
                {deleteConfirm && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
                            <Trash2 className="w-12 h-12 text-rose-500 mx-auto mb-3" /><h3 className="text-lg font-bold">Delete Batch?</h3><p className="text-sm text-slate-500 mt-2">This cannot be undone.</p>
                            <div className="flex gap-3 mt-6"><button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2.5 border rounded-xl text-sm font-medium">Cancel</button>
                                <button onClick={() => deleteMutation.mutate(deleteConfirm)} className="flex-1 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-semibold">Delete</button></div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}